import type { ReportData } from '../../../hooks/useReports';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

interface Props {
  data: ReportData;
  loading: boolean;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtK = (v: number) => v >= 1000 ? `R$${(v/1000).toFixed(1)}k` : `R$${v.toFixed(0)}`;

const CustomTip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 text-sm shadow-sm">
      <p className="font-600 text-gray-700">{d.name}</p>
      <p className="text-[12px] text-gray-500 mt-0.5">{fmt(d.value)}</p>
    </div>
  );
};

export const StatusOriginCharts = ({ data, loading }: Props) => {
  const statusData = [
    { name: 'Pago',     value: data.paidTotal,    color: '#10B981' },
    { name: 'Pendente', value: data.pendingTotal,  color: '#F59E0B' },
    { name: 'Vencido',  value: data.overdueTotal,  color: '#EF4444' },
  ].filter(d => d.value > 0);

  const originData = [
    { name: 'Conta',   value: data.distribConta,  color: '#6366F1' },
    { name: 'Cartão',  value: data.distribCartao, color: '#EC4899' },
  ].filter(d => d.value > 0);

  const recurData = [
    { name: 'Recorrentes', value: data.pctRecorrentes, fill: '#06B6D4' },
    { name: 'Parcelados',  value: data.pctParcelados,  fill: '#8B5CF6' },
    { name: 'Avulsos',     value: Math.max(0, 100 - data.pctRecorrentes - data.pctParcelados), fill: '#E5E7EB' },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1,2,3].map(i => <div key={i} className="h-56 bg-gray-100 rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Status: Pago x Pendente x Vencido */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4">
        <h3 className="text-[14px] font-700 text-gray-900 mb-3">Status dos Lançamentos</h3>
        {statusData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400 text-[12px]">
            <i className="ri-pie-chart-line text-2xl text-gray-200 mb-2" />
            Sem dados
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={32} outerRadius={55} dataKey="value" paddingAngle={2}>
                  {statusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip content={<CustomTip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {statusData.map(d => (
                <div key={d.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-[11px] text-gray-600">{d.name}</span>
                  </div>
                  <span className="text-[11px] font-600 text-gray-800">{fmt(d.value)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Origem: Conta vs Cartão */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4">
        <h3 className="text-[14px] font-700 text-gray-900 mb-3">Gastos por Origem</h3>
        {originData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400 text-[12px]">
            <i className="ri-bank-line text-2xl text-gray-200 mb-2" />
            Sem dados
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={originData} cx="50%" cy="50%" innerRadius={32} outerRadius={55} dataKey="value" paddingAngle={2}>
                  {originData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip content={<CustomTip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {originData.map(d => (
                <div key={d.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-[11px] text-gray-600">{d.name}</span>
                  </div>
                  <span className="text-[11px] font-600 text-gray-800">{fmt(d.value)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Recorrentes vs Parcelados vs Avulsos */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4">
        <h3 className="text-[14px] font-700 text-gray-900 mb-3">Recorrentes vs Avulsos</h3>
        {data.transactionCount === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400 text-[12px]">
            <i className="ri-repeat-line text-2xl text-gray-200 mb-2" />Sem dados
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={recurData} barSize={36} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} width={80} />
                <Tooltip formatter={(v: any) => [`${v}%`]} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {recurData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-2 space-y-1">
              {recurData.filter(d => d.value > 0 && d.name !== 'Avulsos').map(d => (
                <p key={d.name} className="text-[11px] text-gray-500">
                  <span className="font-600 text-gray-700">{d.value}%</span> dos gastos são <span className="font-500">{d.name.toLowerCase()}</span>
                </p>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
